import type { ShippingAddress } from '@/features/order/orderSlice';
import type { SupervisorSnapshotPayload } from '@/features/supervisor/supervisorSlice';
import { setOrderState } from '@/features/order/orderSlice';
import type { EtsyProductionOrderPayload } from '@/services/etsyService';
import {
  activateWorkspaceDraft,
  fetchActiveWorkspaceDraft,
  saveWorkspaceDraft,
  type WorkspaceDraft,
} from '@/services/workspaceDraftService';
import {
  createInitialSupervisorSnapshotPayload,
  getCurrentSupervisorSnapshotPayload,
} from '@/services/supervisorService';

const STUDIO_DRAFT_TITLE = 'Studio draft';

type PersistedOrderState = NonNullable<Parameters<typeof setOrderState>[0]>;

const normalizeShippingAddress = (
  address: EtsyProductionOrderPayload['shippingAddress'],
): ShippingAddress | undefined => {
  if (!address) {
    return undefined;
  }

  return {
    countryCode: address.countryCode ?? '',
    firstName: address.firstName ?? '',
    lastName: address.lastName ?? '',
    phone: address.phone ?? '',
    street1: address.street1 ?? '',
    street2: address.street2 ?? undefined,
    city: address.city ?? '',
    novaposhtaCityRef: address.novaposhtaCityRef ?? undefined,
    novaposhtaWarehouseId: address.novaposhtaWarehouseId ?? undefined,
    deliveryMode: address.deliveryMode ?? undefined,
    shippingProvider: address.shippingProvider ?? undefined,
    region: address.region ?? '',
    postalCode: address.postalCode ?? '',
    setAsDefault: Boolean(address.isDefault),
  };
};

const buildDraftOrderState = (order: EtsyProductionOrderPayload): PersistedOrderState => ({
  id: order.id ?? undefined,
  status: order.status,
  shippingAddress: normalizeShippingAddress(order.shippingAddress),
  fulfillmentMode: ((order as { fulfillmentMode?: PersistedOrderState['fulfillmentMode'] }).fulfillmentMode ?? 'SHIPPING'),
  paymentInformation: (order.paymentInformation ?? undefined) as PersistedOrderState['paymentInformation'],
  items: order.items.map((item) => ({
    id: item.id,
    orderId: item.orderId ?? undefined,
    uploadedFileId: item.uploadedFileId ?? undefined,
    paperTypeId: item.paperTypeId ?? undefined,
    printerId: item.printerId ?? undefined,
    widthIn: item.widthIn ?? undefined,
    heightIn: item.heightIn ?? undefined,
    marginIn: item.marginIn ?? undefined,
    colorMode: item.colorMode ?? undefined,
    status: item.status,
    originalWidthPx: item.originalWidthPx ?? undefined,
    originalHeightPx: item.originalHeightPx ?? undefined,
    originalWidthIn: item.originalWidthIn ?? undefined,
    originalHeightIn: item.originalHeightIn ?? undefined,
    dpiX: item.dpiX ?? undefined,
    dpiY: item.dpiY ?? undefined,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    price: typeof item.priceUsd === 'number'
      ? { value: item.priceUsd, currency: 'USD' }
      : undefined,
  })),
  selectedItemIds: [],
  dirtyFieldsByItemId: {},
  lastSavedFieldValuesByItemId: {},
  pricingSummary: undefined,
  shippingQuote: undefined,
  shippingQuoteStatus: 'idle',
  shippingQuoteError: null,
  packagingSummary: undefined,
  checkoutPersistenceInFlight: false,
});

export const hasMeaningfulSupervisorSnapshot = (snapshot: SupervisorSnapshotPayload | null): boolean => {
  if (!snapshot) {
    return false;
  }

  const items = Array.isArray(snapshot.context.order?.items) ? snapshot.context.order.items : [];
  const hasShippingAddress = Boolean(snapshot.context.order?.shippingAddress);

  return items.length > 0 || hasShippingAddress || snapshot.context.activeStep !== 'upload';
};

export const extractDraftSupervisorSnapshot = (value: unknown): SupervisorSnapshotPayload | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = (value as Record<string, unknown>).supervisor;
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  return candidate as SupervisorSnapshotPayload;
};

export const buildProductionSupervisorSnapshot = (
  order: EtsyProductionOrderPayload,
): SupervisorSnapshotPayload => {
  const initialSnapshot = createInitialSupervisorSnapshotPayload();

  return {
    ...initialSnapshot,
    value: { screen: 'upload' },
    context: {
      ...initialSnapshot.context,
      order: buildDraftOrderState(order) as SupervisorSnapshotPayload['context']['order'],
      activeStep: 'upload',
    },
  };
};

export const hydrateProductionSupervisorSnapshot = (
  snapshot: SupervisorSnapshotPayload,
  order: EtsyProductionOrderPayload,
): SupervisorSnapshotPayload => {
  return {
    ...snapshot,
    context: {
      ...snapshot.context,
      order: buildDraftOrderState(order) as SupervisorSnapshotPayload['context']['order'],
    },
  };
};

export const persistCurrentActiveWorkspaceDraft = async (): Promise<WorkspaceDraft | null> => {
  const supervisorSnapshot = getCurrentSupervisorSnapshotPayload();
  const activeDraft = await fetchActiveWorkspaceDraft();

  if (activeDraft) {
    return saveWorkspaceDraft(activeDraft.id, {
      snapshot: { supervisor: supervisorSnapshot },
    });
  }

  if (!hasMeaningfulSupervisorSnapshot(supervisorSnapshot)) {
    return null;
  }

  return activateWorkspaceDraft({
    source: 'INTERNAL',
    title: STUDIO_DRAFT_TITLE,
    snapshot: { supervisor: supervisorSnapshot },
    metadata: { kind: 'studio' },
  });
};
